// ProgressCircle Component

/* eslint indent: 0 */

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import * as d3 from 'd3'
import Util from '../../../lib/util.jsx'

import Dropdown from '../../form/dropdown/dropdown.jsx'
import TimeClock from '../../../time/time/time_clock/time_clock.jsx'

import { buildTimer } from '../../timer/timer'

class ProgressCircle extends Component {
  static get propTypes () {
    return {
      type: PropTypes.oneOf(['normal', 'warning']),
      value: PropTypes.number,
      totalValue: PropTypes.number,
      outerColor: PropTypes.string,
      innerColor: PropTypes.string,
      dropValue: PropTypes.string,
      sortAsc: PropTypes.bool,
      options: PropTypes.array,
      bShowTime: PropTypes.bool,
      borderWidth: PropTypes.string,
      startAngle: PropTypes.number,
      onSort: PropTypes.func,
      timer: PropTypes.object,
      showTimer: PropTypes.bool,
      subTime: PropTypes.string,
      subInfo: PropTypes.string,
      info: PropTypes.string,
      height: PropTypes.string,
      width: PropTypes.string,
      timeSpent: PropTypes.number,
      formatOverdue: PropTypes.bool,
      onClickCircle: PropTypes.func,
      stopAndCreateNewTimeCard: PropTypes.func
    }
  }

  static get defaultProps () {
    return {
      type: 'normal',
      value: 0,
      totalValue: 100,
      outerColor: '#dddddd',
      innerColor: '',
      dropValue: null,
      sortAsc: true,
      options: [],
      bShowTime: false,
      borderWidth: '',
      startAngle: 0,
      onSort: null,
      timer: buildTimer(null),
      showTimer: true,
      subTime: '',
      subInfo: '',
      info: '',
      height: '',
      width: '',
      timeSpent: 0,
      formatOverdue: false,
      onClickCircle: null,
      stopAndCreateNewTimeCard: null
    }
  }

  constructor (props) {
    super(props)

    this.animation = {
      delay: 350,
      duration: 1000
    }

    this.data = {
      startValue: 0,
      value: props.value
    }

    this._references = {
      graph: React.createRef()
    }
  }

  componentDidMount () {
    this.createArc(this.props)
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps (nextProps) {
    if (nextProps.value !== this.data.value) {
      this.data.startValue = this.props.value
      this.data.value = nextProps.value

      this.createArc(nextProps)
    }
  }

  _onClickCircle (event) {
    event.preventDefault()

    if (this.props.onClickCircle) {
      this.props.onClickCircle()
    }
  }

  createArc (props) {
    let totalValue = 100
    if (props.totalValue) {
      totalValue = props.totalValue
    }

    const tau = 2 * Math.PI
    const startData = this.data.startValue / totalValue
    let data = this.data.value / totalValue
    const svg = d3.select(this._references.graph.current)
    const width = svg.attr('width') / 2
    const height = svg.attr('height') / 2
    const borderWidth = props.borderWidth ? props.borderWidth : '4'
    let outerColor = props.outerColor ? props.outerColor : '#dddddd'
    let innerColor = props.innerColor
      ? props.innerColor
      : props.type === 'normal'
      ? '#4dbce9'
      : '#ff6565'

    let dataPercent = data * 100
    if (this.props.formatOverdue && dataPercent > 100) {
      dataPercent = dataPercent < 200 ? dataPercent - 100 : 100
      data = dataPercent / 100

      outerColor = '#4dbce9'
      innerColor = '#ff6565'
    }

    svg.html('')

    const g = svg
      .append('g')
      .attr('transform', `translate(${width}, ${height})`)
    const arc = d3
      .arc()
      .innerRadius(width - borderWidth)
      .outerRadius(width)
      .startAngle(this.props.startAngle)

    g.append('path')
      .datum({ endAngle: tau + this.props.startAngle })
      .attr('fill', outerColor)
      .attr('d', arc)

    g.append('path')
      .datum({ endAngle: startData * tau + this.props.startAngle })
      .attr('fill', innerColor)
      .attr('d', arc)
      .transition()
      .delay(this.animation.delay)
      .duration(this.animation.duration)
      .attrTween('d', this.arcTween(data * tau + this.props.startAngle, arc))
  }

  arcTween (newAngle, arc) {
    return d => {
      const interpolate = d3.interpolate(d.endAngle, newAngle)

      return t => {
        d.endAngle = interpolate(t)
        return arc(d)
      }
    }
  }

  render () {
    const width = this.props.width ? this.props.width : '115'
    const height = this.props.height ? this.props.height : '115'
    const {
      value,
      info,
      subTime,
      subInfo,
      options,
      dropValue,
      bShowTime,
      showTimer,
      timer,
      timeSpent
    } = this.props
    const graphValue = bShowTime
      ? Util.convertMinutesToHour(value)
      : `${value}%`
    const bTimerStarted = timer.isStarted()

    return (
      <div className='progress-circle' onClick={this._onClickCircle.bind(this)}>
        <svg width={width} height={height} ref={this._references.graph} />
        <div className='contwrap'>
          {subTime ? <span className='subtitle'>{subTime}</span> : null}
          {subInfo ? <span className='subinfo'>{subInfo}</span> : null}
          <h2 className='progress-circle-value'>
            {this._renderTimeClock(
              bShowTime,
              showTimer,
              bTimerStarted,
              timer,
              graphValue,
              value,
              timeSpent
            )}
          </h2>
          {info ? <span className='info'>{info}</span> : null}
          {options.length > 0 && (
            <Dropdown
              source={options}
              name='sortBy'
              multilingual
              label='common.list.sortByLabel'
              className='gridsort-dropdown'
              value={dropValue}
              onChange={this._onChange.bind(this, 'sortBy')}
            />
          )}
        </div>
      </div>
    )
  }

  /* Private functions */
  _renderTimeClock (
    bShowTime,
    showTimer,
    bTimerStarted,
    timer,
    graphValue,
    value,
    timeSpent
  ) {
    if (bShowTime) {
      if (showTimer) {
        if (bTimerStarted) {
          return (
            <TimeClock
              type='task'
              timer={timer}
              timeSpent={timeSpent}
              stopAndCreateNewTimeCard={this.props.stopAndCreateNewTimeCard}
            />
          )
        }

        return Util.convertMinutesToHour(value)
      }

      return graphValue
    } else if (value && !bShowTime) {
      return `${value}%`
    }

    return '0%'
  }

  /* Event handlers */
  _onChange (name, value) {
    if (this.props.onSort) {
      this.props.onSort(value, !this.props.sortAsc)
    }
  }
}

export default ProgressCircle
